import React, { useState, useEffect } from 'react';
import { useI18next } from 'gatsby-plugin-react-i18next';
import styled from '@emotion/styled';
import axios from 'axios';
import { useSWRInfinite } from 'swr';

import Layout from '@components/layout';
import SEO from '@components/SEO';
import ArticleCard from '@components/Blogs/ArticleCard';
import Spinner from '@components/common/Spinner';
import AuthorHeader from '@components/Blogs/AuthorHeader';

const BLOGS_PER_PAGE = 9;
const fetcher = url => axios.get(url).then(res => res.data);

function AuthorPage(props) {
  const { t, i18n } = useI18next();
  const [authorData, setAuthorData] = useState();

  // eslint-disable-next-line no-unused-vars
  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    index => {
      const author = props.params.Slug || 'invideo';

      let fetcherURL = `https://strapi-cms.invideo.io/blogs/?_start=${
        index * BLOGS_PER_PAGE
      }&_limit=${BLOGS_PER_PAGE}&_where[0][author.Slug]=${author}&_sort=published_at:DESC`;

      return fetcherURL;
    },
    fetcher,
  );

  useEffect(() => {
    const author = props.params.Slug;
    axios
      .get(`https://strapi-cms.invideo.io/users/?_where[Slug]=${author}`)
      .then(res => {
        if (res.data[0]) {
          setAuthorData(res.data[0]);
        }
      });
  }, [props.params.Slug]);

  const blogs = data ? [].concat(...data) : [];

  const loadMore = () => {
    setSize(size + 1);
  };

  return (
    <Layout>
      <SEO
        t={t}
        seo={{
          Title: `${authorData?.Name || ''} - Author | InVideo`,
          Description: 'Author Page',
          RobotIndex: 'index',
          RobotFollow: 'follow',
        }}
      />
      <MainWrapper className="mx-auto my-4">
        <AuthorHeader authorData={authorData} />

        {blogs.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {blogs.map(node => {
              const slugPrefix =
                node.Language === 'EN'
                  ? '/blog/'
                  : `/${node.Language.replace('_', '-').toLowerCase()}/blog/`;
              return (
                <ArticleCard
                  node={node}
                  isFooter={true}
                  key={`node-${node.id}`}
                  slugPrefix={slugPrefix}
                  featureImage={node.FeatureImage?.url}
                  className="blog-card m-4 mx-auto overflow-hidden flex flex-col justify-start"
                />
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center flex-col items-center h-screen">
            <img
              className="m-4 w-48"
              id="brand-logo"
              alt="InVideo"
              src="https://d2nb51khh83712.cloudfront.net/static-assets/images/inv-logo.png"
            />
            <Spinner color="#3399ff" size="4px" />
            <h1 className="text-3xl my-4">Loading Blogs...</h1>
          </div>
        )}

        {
          // if number of blogs is not multiple of 9 i.e we've reached end of list
          Array.isArray(blogs) && blogs.length % BLOGS_PER_PAGE === 0 ? (
            <div className="flex justify-center my-4 mb-8">
              <button
                className="load-more-btn font-semibold"
                disabled={isValidating}
                onClick={loadMore}
                aria-label="Load More"
              >
                {isValidating ? <Spinner /> : t('blog:loadMore')}
              </button>
            </div>
          ) : null
        }
      </MainWrapper>
    </Layout>
  );
}

export default AuthorPage;

const MainWrapper = styled.main`
  max-width: 1160px;

  .blog-card {
    overflow: hidden;
    max-width: 350px;
    .image-thumbnail {
      will-change: transform;
      transition: all 0.3s ease;
      object-fit: cover;
      width: 100%;

      height: 180px;
      &:hover {
        transform: scale(1.1);
      }
    }
    .card-title {
      will-change: color;
      transition: 0.15s ease;
    }
    &:hover {
      .card-title {
        color: #3399ff;
      }
    }
  }

  .load-more-btn {
    position: relative;
    width: 194px;
    height: 45px;
    background: #3399ff;
    color: #fff;
    cursor: pointer;
    text-align: center;
    font-size: 14px;
    text-transform: uppercase;
    border-radius: 10px;
    outline-width: 0;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    will-change: box-shadow, transform, top;
    box-shadow: 0px 2px 4px rgb(45 35 66 / 40%),
      0px 7px 13px -3px rgb(45 35 66 / 30%),
      inset 0px -3px 0px rgb(47, 130, 212);

    &:hover {
      box-shadow: 0 10px 15px rgb(47 130 212 / 20%),
        0px 7px 13px -3px rgb(47 130 212 / 30%), inset 0px -3px 0px #2f82d4;
      transform: translateY(-2px);
    }
  }

  .content-post-grid p {
    font-size: 16px;
    color: #777;
  }

  .author {
    max-width: 1080px;
    .author-image {
      border: 12px solid #fff;
      border-radius: 50%;
      overflow: hidden;
      box-shadow: 0 0 0 4px #211d70;
      margin-right: 25px;
      margin-left: 0;
      max-width: 200px;
      min-width: 200px;
      min-height: 200px;
      max-height: 200px;
      align-self: center;
    }

    .author-content {
      flex-grow: 1;
      .author-website {
        a {
          color: #211d70;
        }

        a:visited,
        a:hover {
          color: #211d70;
        }
      }
      .author-description {
        color: #777;
      }
      .author-social {
        border-top: 4px solid #211d70;
        display: flex;
        justify-content: flex-start;
        gap: 20px;
        width: 100%;
        padding-right: 5px;
        padding-top: 10px;
      }
    }
  }
`;
